export interface MenglianStageFile {
  relative_path: string;
  size_bytes: number;
  content_type: string;
}

export interface MenglianStagedUpload {
  relative_path: string;
  storage_uri: string;
  upload_url: string;
  headers: Record<string, string>;
}

export interface MenglianStageUploadResponse {
  batch_id: string;
  storage_prefix: string;
  uploads: MenglianStagedUpload[];
}

export interface MenglianMultipartInitiatedUpload {
  relative_path: string;
  storage_uri: string;
  upload_id: string;
  part_size_bytes: number;
  total_parts: number;
}

export interface MenglianMultipartInitiateResponse {
  batch_id: string;
  storage_prefix: string;
  uploads: MenglianMultipartInitiatedUpload[];
}

export interface MenglianMultipartSignedPart {
  part_number: number;
  upload_url: string;
  headers: Record<string, string>;
}

export interface MenglianMultipartSignPartsResponse {
  parts: MenglianMultipartSignedPart[];
}

export interface MenglianMultipartCompletedPart {
  part_number: number;
  etag: string;
}

async function readError(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null);
  const detail = typeof body?.detail === 'string' ? body.detail : response.statusText;
  return detail || fallback;
}

export async function stageMenglianDataUploads(
  files: MenglianStageFile[],
): Promise<MenglianStageUploadResponse> {
  const response = await fetch('/api/v1/menglian-data/uploads/stage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files }),
  });
  if (!response.ok) {
    throw new Error(await readError(response, '无法创建上传任务'));
  }
  return response.json();
}

export async function initiateMenglianMultipartUploads(
  files: MenglianStageFile[],
): Promise<MenglianMultipartInitiateResponse> {
  const response = await fetch('/api/v1/menglian-data/uploads/multipart/initiate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files }),
  });
  if (!response.ok) {
    throw new Error(await readError(response, '无法初始化分片上传任务'));
  }
  return response.json();
}

export async function signMenglianMultipartParts(input: {
  storage_uri: string;
  upload_id: string;
  part_numbers: number[];
}): Promise<MenglianMultipartSignPartsResponse> {
  const response = await fetch('/api/v1/menglian-data/uploads/multipart/sign-parts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(await readError(response, '无法签发分片上传地址'));
  }
  return response.json();
}

export async function completeMenglianMultipartUpload(input: {
  storage_uri: string;
  upload_id: string;
  parts: MenglianMultipartCompletedPart[];
}): Promise<void> {
  const response = await fetch('/api/v1/menglian-data/uploads/multipart/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(await readError(response, '无法合并分片上传任务'));
  }
}

export async function abortMenglianMultipartUpload(input: {
  storage_uri: string;
  upload_id: string;
}): Promise<void> {
  const response = await fetch('/api/v1/menglian-data/uploads/multipart/abort', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(await readError(response, '无法取消分片上传任务'));
  }
}

export function uploadMenglianFile(
  file: File,
  staged: MenglianStagedUpload,
  onProgress: (progress: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', staged.upload_url);
    Object.entries(staged.headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      reject(new Error(`上传失败 (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('网络错误，上传失败'));
    xhr.onabort = () => reject(new Error('上传已取消'));
    xhr.send(file);
  });
}

export function uploadMenglianPart(
  part: Blob,
  signedPart: MenglianMultipartSignedPart,
): Promise<{ etag: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedPart.upload_url);
    Object.entries(signedPart.headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        if (!etag) {
          reject(new Error('存储服务没有返回分片 ETag'));
          return;
        }
        resolve({ etag });
        return;
      }
      reject(new Error(`分片上传失败 (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('网络错误，分片上传失败'));
    xhr.onabort = () => reject(new Error('分片上传已取消'));
    xhr.send(part);
  });
}
